// Staff review surface for contract submissions (Studio). Left: index of recent
// submissions (artifact-card shape → the eventual pane-2 chronology card). Right:
// the hand-off memo (pane-3 stem + body). Sandbox home for the memo; the eventual
// home is the matter view.
import { useEffect, useState } from 'react'
import { api, ensureCsrf } from '../api'
import ApprovalsConsole, { type ApprovalsData } from '../components/ApprovalsConsole'

type Row = {
  id: number; submitter_name: string; submitter_email: string; subject: string
  kind: string; date: string; created: string; chat_status: string; format_gate: string
  recommendation: string; has_memo: boolean
}
type Check = { status: 'ok' | 'attn' | 'block' | 'na'; label: string; note: string; link?: boolean; source?: 'secretary' | 'submitter'; children?: Check[] }
type AuthorTally = { author: string; count: number }
type MarkupComment = { id: string; author: string; content: string; quoted_text: string }
type MarkupRedline = { id: string; action: string; author: string; text: string; context: string }
type Markup = {
  counts?: { comments: number; redlines: number }
  comment_authors?: AuthorTally[]; redline_authors?: AuthorTally[]
  comments?: MarkupComment[]; redlines?: MarkupRedline[]
  frozen_at?: string; filename?: string
}
type Approvals = ApprovalsData
type Detail = Row & {
  summary: string; stem: string; verdict: string; ready: boolean | null
  headlines: string[]; checklist: Check[]
  it_required: boolean | null; submitter_authored_markup: boolean | null
  markup?: Markup; outward_hidden?: string[]; approvals?: Approvals
  answers: Record<string, { answer: string }>; transcript: { role: string; text: string }[]
}

const CHECK_STYLE: Record<string, { icon: string; color: string }> = {
  ok: { icon: '✓', color: '#16a34a' },
  attn: { icon: '!', color: '#ca8a04' },
  block: { icon: '✕', color: '#dc2626' },
  na: { icon: '–', color: '#9ca3af' },
}

function recPill(rec: string) {
  if (rec === 'follow_up') return { label: 'Follow up', bg: 'var(--me-urgentBg)', fg: 'var(--me-urgent)' }
  if (rec === 'ready') return { label: 'Ready for review', bg: 'var(--me-freshBg)', fg: 'var(--me-fresh)' }
  return { label: rec || '—', bg: 'var(--me-panel)', fg: 'var(--me-textMute)' }
}

// "Anderson, Jill" → "Jill"; "Jill Anderson" → "Jill". For the memo legend.
function firstName(name: string): string {
  const n = (name || '').trim()
  if (!n) return 'the submitter'
  if (n.includes(',')) return n.split(',')[1].trim().split(/\s+/)[0] || 'the submitter'
  return n.split(/\s+/)[0]
}

// Step 3 ("the turn outward") doesn't have a home yet — the counterparty copy
// belongs AFTER OGC reviews + curates, not at intake. Parked greyed-out here as a
// reminder that the engine is built (Feature 2). Flip to true when the OGC-review
// stage exists.
const OUTWARD_ENABLED = false

const REDLINE_LABEL: Record<string, { label: string; color: string }> = {
  insert: { label: 'inserted', color: '#16a34a' },
  delete: { label: 'deleted', color: '#dc2626' },
  move_from: { label: 'moved from', color: '#ca8a04' },
  move_to: { label: 'moved to', color: '#ca8a04' },
}

function authorLine(tally: AuthorTally[]): string {
  return tally.map((t) => `${t.author || '(unknown)'} (${t.count})`).join(' · ')
}

// Feature 2 — the two versions. Top: the "warts-and-all" markup exactly as the
// contract arrived (authors untouched, never re-attributed). Bottom: curate which
// comments to hide, then download the counterparty copy (that doc minus those
// comments; tracked changes + text left exactly as-is).
function MarkupPanel({ markup, subId, initialHidden }: { markup?: Markup; subId: number; initialHidden: string[] }) {
  const [open, setOpen] = useState(false)
  const [hidden, setHidden] = useState<Set<string>>(new Set())
  const [dlBusy, setDlBusy] = useState(false)
  const [dlErr, setDlErr] = useState('')

  // Reset the curation to the submission's recorded choice whenever we switch rows.
  useEffect(() => { setHidden(new Set(initialHidden)); setDlErr('') }, [subId, initialHidden])

  if (!markup || !markup.counts) return null
  const { comments = 0, redlines = 0 } = markup.counts
  const none = comments === 0 && redlines === 0
  const toggle = (id: string) =>
    setHidden((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n })

  const downloadOutward = async () => {
    setDlBusy(true); setDlErr('')
    try {
      await ensureCsrf()
      const r = await api.post(`/contract-submissions/${subId}/outward-docx/`,
        { hide_ids: [...hidden] }, { responseType: 'blob' })
      const cd = (r.headers['content-disposition'] as string) || ''
      const m = /filename="?([^"]+)"?/.exec(cd)
      const fname = m ? m[1] : 'agreement (for counterparty).docx'
      const url = URL.createObjectURL(r.data as Blob)
      const a = document.createElement('a')
      a.href = url; a.download = fname
      document.body.appendChild(a); a.click(); a.remove()
      URL.revokeObjectURL(url)
    } catch {
      setDlErr('Could not build the counterparty copy.')
    } finally { setDlBusy(false) }
  }

  return (
    <div className="mt-8 rounded-xl p-4" style={{ background: 'var(--me-surface)', border: '1px solid var(--me-border)' }}>
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-semibold">Markup at submission — warts and all</div>
          <div className="text-xs mt-0.5" style={{ color: 'var(--me-textMute)' }}>
            {none ? 'No comments or tracked changes in the submitted file.'
              : `${comments} comment${comments === 1 ? '' : 's'} · ${redlines} tracked change${redlines === 1 ? '' : 's'}, exactly as received`}
          </div>
        </div>
        {!none && (
          <button onClick={() => setOpen((o) => !o)} className="text-xs" style={{ color: 'var(--me-textMute)' }}>
            {open ? 'hide' : 'show all'}
          </button>
        )}
      </div>

      {!none && (
        <div className="mt-3 text-xs space-y-1" style={{ color: 'var(--me-textDim)' }}>
          {(markup.comment_authors?.length ?? 0) > 0 && (
            <div><span style={{ color: 'var(--me-textMute)' }}>Comments by:</span> {authorLine(markup.comment_authors!)}</div>
          )}
          {(markup.redline_authors?.length ?? 0) > 0 && (
            <div><span style={{ color: 'var(--me-textMute)' }}>Changes by:</span> {authorLine(markup.redline_authors!)}</div>
          )}
        </div>
      )}

      {open && !none && (
        <div className="mt-4 space-y-5">
          {(markup.comments?.length ?? 0) > 0 && (
            <div>
              <div className="text-xs font-semibold mb-2" style={{ color: 'var(--me-textMute)' }}>
                Comments <span style={{ fontWeight: 400 }}>{OUTWARD_ENABLED
                  ? '— tick any to hide from the counterparty copy'
                  : '— exactly as received'}</span>
              </div>
              <div className="space-y-2 max-h-80 overflow-auto pr-1">
                {markup.comments!.map((c) => {
                  const isHidden = hidden.has(c.id)
                  return (
                    <label key={`c${c.id}`} className="flex gap-2 text-[13px]"
                      style={{ opacity: isHidden ? 0.5 : 1, cursor: OUTWARD_ENABLED ? 'pointer' : 'default' }}>
                      <input type="checkbox" className="mt-1 shrink-0" checked={isHidden}
                        disabled={!OUTWARD_ENABLED} onChange={() => toggle(c.id)} />
                      <div style={{ textDecoration: isHidden ? 'line-through' : 'none' }}>
                        <span className="font-medium">{c.author || '(unknown)'}</span>
                        <span style={{ color: 'var(--me-text)' }}>: {c.content}</span>
                        {c.quoted_text && (
                          <div className="mt-0.5 pl-3 border-l-2" style={{ borderColor: 'var(--me-border)', color: 'var(--me-textMute)' }}>
                            on “{c.quoted_text.length > 160 ? c.quoted_text.slice(0, 160) + '…' : c.quoted_text}”
                          </div>
                        )}
                      </div>
                    </label>
                  )
                })}
              </div>
            </div>
          )}
          {(markup.redlines?.length ?? 0) > 0 && (
            <div>
              <div className="text-xs font-semibold mb-2" style={{ color: 'var(--me-textMute)' }}>
                Tracked changes <span style={{ fontWeight: 400 }}>— kept as-is in both versions</span>
              </div>
              <div className="space-y-1.5 max-h-80 overflow-auto pr-1">
                {markup.redlines!.map((r) => {
                  const rl = REDLINE_LABEL[r.action] || { label: r.action, color: 'var(--me-textMute)' }
                  return (
                    <div key={`r${r.id}`} className="text-[13px]">
                      <span style={{ color: rl.color }}>{rl.label}</span>
                      <span style={{ color: 'var(--me-textMute)' }}> by {r.author || '(unknown)'}</span>
                      <span style={{ color: 'var(--me-text)' }}>: {r.text.length > 140 ? r.text.slice(0, 140) + '…' : r.text}</span>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {comments > 0 && (
        <div className="mt-5 pt-4 flex items-center gap-3" style={{ borderTop: '1px solid var(--me-border)' }}>
          <button onClick={OUTWARD_ENABLED ? downloadOutward : undefined} disabled={!OUTWARD_ENABLED || dlBusy}
            title={OUTWARD_ENABLED
              ? 'The submitted document with the ticked comments removed; tracked changes and text unchanged'
              : 'Comes at the OGC-review stage — after GC reviews and comments. Engine is built.'}
            className="rounded-lg px-3 py-2 text-sm font-medium"
            style={{ background: 'var(--me-panel)', border: '1px solid var(--me-border)', color: 'var(--me-text)',
                     opacity: OUTWARD_ENABLED ? 1 : 0.4, cursor: OUTWARD_ENABLED ? 'pointer' : 'not-allowed' }}>
            {!OUTWARD_ENABLED ? 'Counterparty copy — in the oven'
              : dlBusy ? 'Building…'
                : hidden.size > 0
                  ? `Download counterparty copy (hiding ${hidden.size} comment${hidden.size === 1 ? '' : 's'})`
                  : 'Download counterparty copy'}
          </button>
          {!OUTWARD_ENABLED ? (
            <span className="text-xs" style={{ color: 'var(--me-textMute)' }}>
              curation happens at the OGC-review stage, after GC comments — mechanism built, parked here as a reminder
            </span>
          ) : hidden.size === 0 && !open ? (
            <span className="text-xs" style={{ color: 'var(--me-textMute)' }}>open “show all” to choose comments to hide</span>
          ) : null}
          {dlErr && <span className="text-xs" style={{ color: 'var(--me-urgent)' }}>{dlErr}</span>}
        </div>
      )}
    </div>
  )
}

type Cand = { id: number; subject: string; from_name: string; from_email: string; date: string; attachment: string; format: string }
type StartResult = { ok: boolean; bounce?: boolean; chat_url?: string; submitter_name?: string; kind?: string; format_gate?: string; reason?: string }

export default function ContractSubmissions() {
  const [rows, setRows] = useState<Row[]>([])
  const [sel, setSel] = useState<number | null>(null)
  const [detail, setDetail] = useState<Detail | null>(null)
  const [busy, setBusy] = useState(false)
  // Demo picker: start a fresh intake from a real contract email.
  const [pickerOpen, setPickerOpen] = useState(false)
  const [cands, setCands] = useState<Cand[]>([])
  const [starting, setStarting] = useState(false)
  const [startResult, setStartResult] = useState<StartResult | null>(null)

  const loadRows = () =>
    api.get('/contract-submissions/').then((r) => {
      setRows(r.data)
      setSel((cur) => cur ?? (r.data.length ? r.data[0].id : null))
    }).catch(() => {})

  const loadCands = () =>
    api.get('/contract-submissions/candidates/').then((r) => setCands(r.data)).catch(() => {})
  const openPicker = () => { setPickerOpen(true); setStartResult(null); loadCands() }
  const startFromEmail = async (id: number) => {
    setStarting(true); setStartResult(null)
    try {
      const r = await api.post('/contract-submissions/start-from-email/', { email_id: id })
      setStartResult(r.data)
      loadRows()  // new intake (chat OR bounce) shows at the top of the index
    }
    finally { setStarting(false) }
  }

  const deleteOne = async (id: number) => {
    await api.delete(`/contract-submissions/${id}/`)
    setRows((rs) => rs.filter((x) => x.id !== id))
    if (sel === id) { setSel(null); setDetail(null) }
  }
  const clearAll = async () => {
    if (!window.confirm('Clear all contract submissions? This permanently deletes them from the list.')) return
    await api.post('/contract-submissions/clear/')
    setRows([]); setSel(null); setDetail(null)
  }

  // Browser-tab label so the memo tab is easy to tell apart.
  useEffect(() => { document.title = 'MemoCC' }, [])

  useEffect(() => { loadRows() }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (sel == null) return
    setDetail(null)
    api.get(`/contract-submissions/${sel}/`).then((r) => setDetail(r.data)).catch(() => {})
  }, [sel])

  const rebuild = async () => {
    if (sel == null) return
    setBusy(true)
    try { const r = await api.post(`/contract-submissions/${sel}/rebuild-memo/`); setDetail(r.data) }
    finally { setBusy(false) }
  }

  // Feature 1 — hand OGC a Word copy for review. The backend returns the submitted
  // .docx as-is, or exports/downloads a Google-linked doc as Word. 404 = no Word copy.
  const [dlBusy, setDlBusy] = useState(false)
  const [dlErr, setDlErr] = useState('')
  const downloadWordForOgc = async () => {
    if (sel == null) return
    setDlBusy(true); setDlErr('')
    try {
      const r = await api.get(`/contract-submissions/${sel}/review-docx/`, { responseType: 'blob' })
      const cd = (r.headers['content-disposition'] as string) || ''
      const m = /filename="?([^"]+)"?/.exec(cd)
      const fname = m ? m[1] : 'agreement.docx'
      const url = URL.createObjectURL(r.data as Blob)
      const a = document.createElement('a')
      a.href = url; a.download = fname
      document.body.appendChild(a); a.click(); a.remove()
      URL.revokeObjectURL(url)
    } catch (e) {
      const st = (e as { response?: { status?: number } })?.response?.status
      setDlErr(st === 404 ? 'No Word copy available for this submission.' : 'Download failed.')
    } finally { setDlBusy(false) }
  }

  return (
    <div className="flex h-full" style={{ color: 'var(--me-text)' }}>
      {/* Demo picker modal: pick a real contract email → get a fresh chat link */}
      {pickerOpen && (
        <div className="fixed inset-0 z-50 grid place-items-center px-4"
             style={{ background: 'rgba(0,0,0,.45)' }} onClick={() => setPickerOpen(false)}>
          <div className="w-[580px] max-h-[82vh] rounded-2xl overflow-hidden flex flex-col shadow-xl"
               style={{ background: 'var(--me-panel)', border: '1px solid var(--me-border)' }}
               onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-3 border-b flex items-center justify-between" style={{ borderColor: 'var(--me-border)' }}>
              <div className="font-semibold">Start a contract intake</div>
              <button onClick={() => setPickerOpen(false)} className="text-sm" style={{ color: 'var(--me-textMute)' }}>close</button>
            </div>
            {startResult ? (
              <div className="p-5">
                {startResult.ok ? (
                  <>
                    <div className="text-sm mb-3">
                      {startResult.bounce ? (
                        <>Format bounce created for <b>{startResult.submitter_name || 'the submitter'}</b>. The link
                        below is the <b>submitter's bounce page</b> — what they see when a non-Word file is rejected.
                        It's now in the list as a bounced submission.</>
                      ) : (
                        <>Fresh intake ready for <b>{startResult.submitter_name || 'the submitter'}</b>
                        {startResult.kind ? <> — <span style={{ color: 'var(--me-textMute)' }}>{startResult.kind}</span></> : null}.</>
                      )}
                    </div>
                    <div className="text-xs mb-1" style={{ color: 'var(--me-textMute)' }}>
                      {startResult.bounce ? 'Submitter bounce-page link' : 'Submitter chat link'}
                    </div>
                    <div className="flex gap-2">
                      <input readOnly value={startResult.chat_url}
                             onFocus={(e) => e.currentTarget.select()}
                             className="flex-1 rounded px-2 py-1.5 text-xs"
                             style={{ background: 'var(--me-surface)', border: '1px solid var(--me-border)', color: 'var(--me-text)' }} />
                      <a href={startResult.chat_url} target="_blank" rel="noreferrer"
                         className="rounded px-3 py-1.5 text-sm font-medium text-white"
                         style={{ background: 'var(--me-accent, #2563eb)' }}>open</a>
                    </div>
                  </>
                ) : (
                  <div className="text-sm">Couldn't start — {startResult.reason || `format gate: ${startResult.format_gate}`}</div>
                )}
                <button onClick={() => setStartResult(null)} className="mt-4 text-xs" style={{ color: 'var(--me-textMute)' }}>← pick another</button>
              </div>
            ) : (
              <>
                <div className="px-4 py-2 text-xs border-b" style={{ borderColor: 'var(--me-border)', color: 'var(--me-textMute)' }}>
                  Pick a contract to start a live intake.
                </div>
                <div className="overflow-y-auto">
                  {cands.length === 0 && (
                    <div className="p-5 text-sm" style={{ color: 'var(--me-textMute)' }}>No demo items configured.</div>
                  )}
                  {cands.map((c) => (
                    <button key={c.id} disabled={starting} onClick={() => startFromEmail(c.id)}
                            className="w-full text-left px-4 py-2.5 border-b disabled:opacity-50"
                            style={{ borderColor: 'var(--me-border)' }}>
                      <div className="text-sm font-medium leading-snug">{c.subject}</div>
                      <div className="text-xs mt-0.5" style={{ color: 'var(--me-textMute)' }}>
                        {c.from_name || c.from_email} · {c.date}{c.attachment ? ` · ${c.attachment}` : ''}
                      </div>
                    </button>
                  ))}
                  {starting && <div className="p-4 text-xs" style={{ color: 'var(--me-textMute)' }}>starting…</div>}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Index — the eventual pane-2 chronology cards */}
      <div className="w-80 shrink-0 overflow-y-auto border-r" style={{ borderColor: 'var(--me-border)' }}>
        <div className="px-4 py-3 flex items-center justify-between border-b" style={{ borderColor: 'var(--me-border)' }}>
          <span className="text-sm font-semibold" style={{ color: 'var(--me-textMute)' }}>Contract submissions</span>
          <div className="flex items-center gap-2">
            {rows.length > 0 && (
              <button onClick={clearAll} className="text-xs" style={{ color: 'var(--me-textMute)' }}>Clear</button>
            )}
            <button onClick={openPicker} className="text-xs rounded px-2 py-1 font-medium text-white"
                    style={{ background: 'var(--me-accent, #2563eb)' }}>+ New intake</button>
          </div>
        </div>
        {rows.length === 0 && (
          <div className="px-4 py-6 text-sm" style={{ color: 'var(--me-textMute)' }}>No submissions yet.</div>
        )}
        {rows.map((r) => {
          const pill = recPill(r.recommendation)
          return (
            <div key={r.id} className="relative border-b group"
                 style={{ borderColor: 'var(--me-border)',
                          background: sel === r.id ? 'var(--me-selected)' : 'transparent' }}>
              <button onClick={() => setSel(r.id)} className="w-full text-left px-4 py-3 pr-8">
                <div className="text-sm font-medium leading-snug">{r.subject}</div>
                <div className="text-xs mt-1" style={{ color: 'var(--me-textMute)' }}>
                  Submitted by {r.submitter_name || '—'}{r.created ? ` · ${r.created}` : ''}
                </div>
                <div className="mt-1.5 flex items-center gap-1.5">
                  {r.has_memo && (
                    <span className="text-[11px] px-1.5 py-0.5 rounded"
                          style={{ background: pill.bg, color: pill.fg }}>{pill.label}</span>
                  )}
                  <span className="text-[11px] px-1.5 py-0.5 rounded"
                        style={{ background: 'var(--me-panel)', color: 'var(--me-textMute)' }}>{r.chat_status}</span>
                </div>
              </button>
              <button onClick={() => deleteOne(r.id)} title="Delete this submission"
                      className="absolute top-2.5 right-2 leading-none opacity-0 group-hover:opacity-100 transition-opacity"
                      style={{ color: 'var(--me-textMute)' }}>✕</button>
            </div>
          )
        })}
      </div>

      {/* Memo */}
      <div className="flex-1 overflow-y-auto">
        {!detail && (
          <div className="grid place-items-center h-full">
            <div className="size-7 rounded-full border-[3px] border-[color:var(--me-textMute)] border-t-transparent animate-spin" />
          </div>
        )}
        {detail && (
          <div className="max-w-3xl mx-auto px-6 py-6">
            {/* submission card */}
            <div className="rounded-xl p-4 mb-6" style={{ background: 'var(--me-surface)', border: '1px solid var(--me-border)' }}>
              <div className="font-semibold">{detail.subject}</div>
              <div className="text-xs mt-0.5" style={{ color: 'var(--me-textMute)' }}>
                Submitted by {detail.submitter_name || '—'}{detail.created ? ` · ${detail.created}` : ''}
              </div>
              <div className="text-sm mt-2" style={{ color: 'var(--me-textDim)' }}>{detail.summary}</div>
            </div>

            {/* Approvals — the paralegal's pre-GC intake console, pinned at the top. */}
            <ApprovalsConsole data={detail.approvals} />

            {detail.has_memo ? (
              <>
                {/* verdict (the conclusion — first thing the reviewer reads) */}
                <div className="text-lg font-semibold"
                     style={{ color: detail.ready ? '#16a34a' : '#ca8a04' }}>
                  {detail.verdict}
                </div>
                {/* the checklist */}
                <div className="mt-5 space-y-2">
                  {detail.checklist.map((c, i) => {
                    const st = CHECK_STYLE[c.status] || CHECK_STYLE.na
                    return (
                      <div key={i}>
                        <div className="flex gap-2.5 text-[14.5px]">
                          <span className="inline-grid place-items-center rounded-full shrink-0 text-white text-[8px] font-bold"
                                style={{ width: 13, height: 13, background: st.color, marginTop: 3 }}>{st.icon}</span>
                          <div>
                            <span className="font-medium">{c.label}</span>
                            {c.note && (c.link
                              // VP row: live mailto link ("email the VP"), grey like the submitter note.
                              ? <a href="mailto:" style={{ color: 'var(--me-textDim)', textDecoration: 'underline' }}>{' '}{c.note}</a>
                              // Source color: secretary (system read) = blue, submitter (chat) = dark grey.
                              : <span style={{ color: c.source === 'submitter' ? 'var(--me-textDim)' : '#2563eb' }}>{' '}{c.note}</span>)}
                          </div>
                        </div>
                        {c.children && c.children.length > 0 && (
                          <div className="ml-8 mt-1 space-y-1">
                            {c.children.map((k, j) => {
                              const kst = CHECK_STYLE[k.status] || CHECK_STYLE.na
                              return (
                                <div key={j} className="flex gap-2 text-[13px]">
                                  <span className="mt-1 inline-block rounded-full shrink-0"
                                        style={{ width: 8, height: 8, background: kst.color }} />
                                  <div style={{ color: 'var(--me-textDim)' }}>
                                    {k.label}{k.note && <span style={{ color: '#2563eb' }}>{' '}{k.note}</span>}
                                  </div>
                                </div>
                              )
                            })}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
                {/* Legend — decode the note colors (Ken 2026-10-01). */}
                <div className="mt-6 text-xs leading-relaxed">
                  <div style={{ color: '#2563eb' }}>determined by Secretary</div>
                  <div style={{ color: 'var(--me-textDim)' }}>attested by {firstName(detail.submitter_name)}</div>
                </div>
              </>
            ) : (
              <div className="mt-3 text-sm" style={{ color: 'var(--me-textMute)' }}>
                No memo yet — it generates when the intake chat completes.
              </div>
            )}

            {/* Feature 2 — warts-and-all markup + the curated counterparty copy. */}
            <MarkupPanel markup={detail.markup} subId={detail.id} initialHidden={detail.outward_hidden || []} />

            <div className="mt-6 flex items-center gap-3">
              <button onClick={rebuild} disabled={busy}
                className="rounded-lg px-3 py-2 text-sm font-medium disabled:opacity-40"
                style={{ background: 'var(--me-panel)', border: '1px solid var(--me-border)', color: 'var(--me-text)' }}>
                {busy ? 'Regenerating…' : 'Regenerate memo'}
              </button>
              {/* Feature 1 — OGC reviews in Word, even when the submission was a Google doc. */}
              <button onClick={downloadWordForOgc} disabled={dlBusy || detail.format_gate !== 'pass'}
                title={detail.format_gate !== 'pass' ? 'Only passing submissions have a review copy' : 'Download a Word (.docx) copy for OGC review'}
                className="rounded-lg px-3 py-2 text-sm font-medium disabled:opacity-40"
                style={{ background: 'var(--me-panel)', border: '1px solid var(--me-border)', color: 'var(--me-text)' }}>
                {dlBusy ? 'Preparing…' : 'Download Word for OGC'}
              </button>
            </div>
            {dlErr && <div className="mt-2 text-xs" style={{ color: 'var(--me-urgent)' }}>{dlErr}</div>}
          </div>
        )}
      </div>
    </div>
  )
}
